// Bandeja -> Conocimiento, diseno «Manual» (elegido por Johan): a la izquierda la portada con colecciones, buscador,
// lo mas consultado y lo que falta revisar; a la derecha el articulo como una guia (ruta, titulo, resumen, etiquetas,
// pasos numerados, codigo con «Copiar», avisos, «Abrir acceso», «¿Te sirvió?» e «Insertar en la respuesta del caso»).
// Direccion: ?articulo=<id> (el articulo abierto), ?col=<ruta "A / B"> (coleccion o subcategoria), ?ver=revisar,
// ?editar=<id>|nuevo (editor).
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { type Articulo, buscarEnServidor, marcarUtil, type Resultado, sumarVista, useArticulos } from '../../api/conocimiento';
import { useDebounced } from '../../hooks';
import { buscarArticulos, colecciones, contenidoDe, fragmentoLocal, iconoColeccion, masConsultados, partesDe, porRevisar, SEP, textoParaRespuesta } from '../../lib/conocimiento';
import { ago, plural } from '../../lib/format';
import { Icon } from '../../lib/icons';
import { insertarEnRespuesta } from '../../lib/insertarEnRespuesta';
import { Empty, ErrorBox, Skeleton } from '../../ui/bits';
import { Fragmento } from '../../ui/Fragmento';
import { Markdown } from '../../ui/Markdown';
import { toast } from '../../ui/toasts';
import { EditorArticulo } from './EditorArticulo';

// Para las pruebas y para quien ya la usaba desde aqui.
export { buscarArticulos } from '../../lib/conocimiento';
export type { Articulo } from '../../api/conocimiento';
export { useArticulos } from '../../api/conocimiento';

const VISTA_TRAS_MS = 5000;

export function Conocimiento() {
  const [sp, setSp] = useSearchParams();
  const id = sp.get('articulo');
  const colTexto = sp.get('col') ?? '';
  const col = useMemo(() => partesDe(colTexto), [colTexto]);
  const ver = sp.get('ver');
  const editarId = sp.get('editar');
  const r = useArticulos();
  const lista = useMemo(() => r.data ?? [], [r.data]);
  const actual = lista.find((a) => a.id === id) ?? null;

  const cambiar = (cambios: Record<string, string | null>) => {
    const n = new URLSearchParams(sp);
    for (const [k, v] of Object.entries(cambios)) {
      if (v) n.set(k, v);
      else n.delete(k);
    }
    setSp(n, { replace: true });
  };
  const abrir = (x: string | null) => cambiar({ articulo: x });
  const irA = (ruta: string[]) => cambiar({ col: ruta.length ? ruta.join(SEP) : null, ver: null });

  if (editarId) {
    const a = editarId === 'nuevo' ? null : lista.find((x) => x.id === editarId);
    if (editarId !== 'nuevo' && !a)
      return r.isLoading ? (
        <div style={{ padding: 24 }}>
          <Skeleton rows={8} />
        </div>
      ) : (
        <Empty icon="book" titulo="Ese artículo no existe" />
      );
    return <EditorArticulo articulo={a ?? null} lista={lista} categoriaInicial={col.join(SEP)} onCerrar={(guardado) => cambiar({ editar: null, articulo: guardado === undefined ? id : guardado })} />;
  }

  return (
    <div className={`kbm ${actual ? 'con-art' : ''}`}>
      <Portada
        lista={lista}
        cargando={r.isLoading}
        error={r.error}
        reintentar={() => r.refetch()}
        col={col}
        ver={ver}
        actualId={id}
        abrir={abrir}
        irA={irA}
        verRevisar={() => cambiar({ ver: 'revisar', col: null })}
        nuevo={() => cambiar({ editar: 'nuevo' })}
      />
      <section className="kbm-art" aria-label="Artículo">
        {actual ? (
          <Lector key={actual.id} a={actual} abrir={abrir} irA={irA} editar={() => cambiar({ editar: actual.id })} />
        ) : id && r.isLoading ? (
          <div style={{ padding: 32 }}>
            <Skeleton rows={10} />
          </div>
        ) : (
          <div className="kbm-vacio">
            <Empty icon="book" titulo={id ? 'Ese artículo ya no existe' : 'Elige un artículo'} texto="Busca a la izquierda o entra a una colección." />
          </div>
        )}
      </section>
    </div>
  );
}

// ---------- Portada (izquierda) ----------

function Portada(p: {
  lista: Articulo[];
  cargando: boolean;
  error: unknown;
  reintentar: () => void;
  col: string[];
  ver: string | null;
  actualId: string | null;
  abrir: (id: string) => void;
  irA: (ruta: string[]) => void;
  verRevisar: () => void;
  nuevo: () => void;
}) {
  const { lista, col, abrir, irA } = p;
  const [q, setQ] = useState('');
  const dq = useDebounced(q.trim(), 250);
  const ambito = col.join(SEP);
  const servidor = useQuery({
    queryKey: ['kb-buscar', dq, ambito],
    queryFn: ({ signal }) => buscarEnServidor(dq, ambito, signal),
    enabled: dq.length >= 2,
    staleTime: 30_000,
  });
  const resultados: Resultado[] | null = useMemo(() => {
    if (dq.length < 2) return null;
    if (servidor.data) return servidor.data.articulos;
    if (servidor.isFetching) return null;
    // Respaldo: el buscador del servidor no respondio.
    const dentro = ambito ? lista.filter((a) => a.categoria === ambito || a.categoria.startsWith(ambito + SEP)) : lista;
    return buscarArticulos(dentro, dq)
      .slice(0, 30)
      .map((a) => ({ id: a.id, titulo: a.titulo, categoria: a.categoria, resumen: a.resumen || null, fragmento: fragmentoLocal(a.cuerpo, dq), vistas: a.vistas }));
  }, [dq, servidor.data, servidor.isFetching, lista, ambito]);

  const cols = useMemo(() => colecciones(lista), [lista]);
  const top = useMemo(() => masConsultados(lista, 5), [lista]);
  const revisar = useMemo(() => lista.filter(porRevisar), [lista]);
  const dentro = useMemo(() => (col.length ? contenidoDe(lista, col) : null), [lista, col]);

  return (
    <aside className="kbm-home" aria-label="Colecciones">
      <div className="kbm-cab">
        <div className="kbm-cab-t">
          <span className="kbm-eyebrow">Manual de Xmartek</span>
          <h1>{col.length ? col[col.length - 1] : p.ver === 'revisar' ? 'Para revisar' : 'Colecciones'}</h1>
        </div>
        <button className="btn sm primary" onClick={p.nuevo}>
          <Icon name="plus" /> Nuevo
        </button>
      </div>
      {col.length || p.ver === 'revisar' ? (
        <nav className="kbm-migas" aria-label="Ruta">
          <button type="button" onClick={() => irA([])}>
            <Icon name="chevLeft" size={13} /> Colecciones
          </button>
          {col.slice(0, -1).map((c, i) => (
            <span key={i}>
              <span aria-hidden="true"> / </span>
              <button type="button" onClick={() => irA(col.slice(0, i + 1))}>
                {c}
              </button>
            </span>
          ))}
        </nav>
      ) : null}
      <label className="kbm-buscar">
        <Icon name="search" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={ambito ? `Buscar en ${col[col.length - 1]}…` : 'Buscar en el manual…'} aria-label="Buscar en el manual" type="search" />
        {q ? (
          <button type="button" className="kbm-limpiar" onClick={() => setQ('')} aria-label="Limpiar búsqueda">
            <Icon name="x" size={14} />
          </button>
        ) : null}
      </label>
      {p.error ? <ErrorBox error={p.error} onRetry={p.reintentar} /> : null}

      {dq.length >= 2 ? (
        <section className="kbm-res" aria-label="Resultados" aria-busy={!resultados || undefined}>
          {!resultados ? (
            <Skeleton rows={5} />
          ) : resultados.length ? (
            <>
              <p className="kbm-sub">
                {plural(resultados.length, 'resultado', 'resultados')}
                {servidor.data?.aproximado ? ' parecidos (revisa cómo lo escribiste)' : ''}
                {ambito ? (
                  <>
                    {' '}
                    en <b>{col[col.length - 1]}</b> ·{' '}
                    <button type="button" className="linkish" onClick={() => irA([])}>
                      buscar en todo
                    </button>
                  </>
                ) : null}
              </p>
              {resultados.map((x) => (
                <button key={x.id} type="button" className="kbm-it" aria-current={x.id === p.actualId ? 'true' : undefined} onClick={() => abrir(x.id)}>
                  <b>{x.titulo}</b>
                  {x.fragmento ? (
                    <span className="kbm-frag">
                      <Fragmento texto={x.fragmento} />
                    </span>
                  ) : x.resumen ? (
                    <span className="kbm-frag">{x.resumen}</span>
                  ) : null}
                  <small>{x.categoria}</small>
                </button>
              ))}
            </>
          ) : (
            <Empty icon="search" titulo="Nada con esa búsqueda" texto="Prueba con otra palabra o menos palabras." />
          )}
        </section>
      ) : p.cargando ? (
        <Skeleton rows={8} height={22} />
      ) : p.ver === 'revisar' ? (
        <section className="kbm-lista" aria-label="Para revisar">
          <p className="kbm-sub">Venían de Zammad solo con adjuntos o casi no tienen texto. Ábrelos y complétalos.</p>
          {revisar.map((a) => (
            <ItemArticulo key={a.id} a={a} actual={a.id === p.actualId} abrir={abrir} conRuta />
          ))}
        </section>
      ) : dentro ? (
        <section className="kbm-lista" aria-label={col.join(SEP)}>
          <p className="kbm-sub">{plural(dentro.total, 'artículo', 'artículos')}</p>
          {dentro.subcategorias.length ? (
            <div className="kbm-subs">
              {dentro.subcategorias.map((s) => (
                <button key={s.nombre} type="button" className="kbm-subcat" onClick={() => irA([...col, s.nombre])}>
                  <Icon name="book" size={15} />
                  <span>{s.nombre}</span>
                  <small>{s.total}</small>
                  <Icon name="chevRight" size={14} />
                </button>
              ))}
            </div>
          ) : null}
          {dentro.articulos.map((a) => (
            <ItemArticulo key={a.id} a={a} actual={a.id === p.actualId} abrir={abrir} />
          ))}
          {!dentro.total ? <Empty icon="book" titulo="Esta colección está vacía" /> : null}
        </section>
      ) : (
        <>
          <div className="kbm-cols">
            {cols.map((c) => {
              const ic = iconoColeccion(c.nombre);
              return (
                <button key={c.nombre} type="button" className="kbm-col" onClick={() => irA([c.nombre])}>
                  <span className={`kbm-col-i t-${ic.tono}`}>
                    <Icon name={ic.icono} size={18} />
                  </span>
                  <b>{c.nombre}</b>
                  <span className="kbm-col-d">
                    {plural(c.total, 'artículo', 'artículos')}
                    {c.detalle.length ? ` · ${c.detalle.join(', ').toLowerCase()}` : ''}
                  </span>
                </button>
              );
            })}
            {!cols.length ? <Empty icon="book" titulo="Aún no hay artículos" texto="Crea el primero con «Nuevo»." /> : null}
          </div>
          {top.length ? (
            <div className="kbm-caja">
              <b>Lo más consultado</b>
              {top.map((a) => (
                <button key={a.id} type="button" className="kbm-link" onClick={() => abrir(a.id)}>
                  {a.titulo}
                </button>
              ))}
            </div>
          ) : null}
          {revisar.length ? (
            <button type="button" className="kbm-revisar" onClick={p.verRevisar}>
              <b>{revisar.length}</b>
              <span>{revisar.length === 1 ? 'artículo venía de Zammad solo con adjuntos o casi sin texto: revisa que esté completo.' : 'artículos venían de Zammad solo con adjuntos o casi sin texto: revisa que estén completos.'}</span>
              <Icon name="chevRight" size={15} />
            </button>
          ) : null}
        </>
      )}
    </aside>
  );
}

function ItemArticulo({ a, actual, abrir, conRuta }: { a: Articulo; actual: boolean; abrir: (id: string) => void; conRuta?: boolean }) {
  return (
    <button type="button" className="kbm-it" aria-current={actual ? 'true' : undefined} onClick={() => abrir(a.id)}>
      <b>{a.titulo}</b>
      {a.resumen ? <span className="kbm-frag">{a.resumen}</span> : null}
      {conRuta ? <small>{a.categoria}</small> : null}
    </button>
  );
}

// ---------- Lector (derecha) ----------

function Lector({ a, abrir, irA, editar }: { a: Articulo; abrir: (id: string | null) => void; irA: (ruta: string[]) => void; editar: () => void }) {
  const nav = useNavigate();
  const ruta = partesDe(a.categoria);
  const [voto, setVoto] = useState<'si' | 'no' | null>(null);
  const caja = useRef<HTMLElement>(null);

  // Una vista cuenta si el articulo queda abierto mas de 5 s.
  useEffect(() => {
    const t = setTimeout(() => void sumarVista(a.id).catch(() => undefined), VISTA_TRAS_MS);
    caja.current?.scrollTo?.({ top: 0 });
    return () => clearTimeout(t);
  }, [a.id]);

  const votar = async (util: boolean) => {
    setVoto(util ? 'si' : 'no');
    try {
      await marcarUtil(a.id, util);
    } catch {
      /* el voto es solo una senal: si falla no se molesta a la persona */
    }
  };

  const insertar = async () => {
    const r = await insertarEnRespuesta(textoParaRespuesta(a, location.origin), (lugar) => nav(lugar));
    // 'insertado' y 'pendiente': el cuadro de respuesta avisa al recibirlo.
    if (r === 'copiado') toast('No hay una respuesta abierta: el texto quedó copiado. Pégalo en la respuesta del caso.');
    else if (r === 'fallo') toast('No se pudo copiar el texto. Insértalo desde la respuesta del caso con «Artículo».', 'error');
  };

  return (
    <article className="kbm-lector" ref={caja}>
      <div className="kbm-lector-in">
        <div className="kbm-barra">
          <button className="btn sm ghost solo-movil-b" onClick={() => abrir(null)} aria-label="Volver a las colecciones">
            <Icon name="chevLeft" /> Volver
          </button>
          <nav className="kbm-ruta" aria-label="Categoría">
            {ruta.map((c, i) => (
              <span key={i}>
                {i ? <span aria-hidden="true"> / </span> : null}
                <button type="button" onClick={() => irA(ruta.slice(0, i + 1))}>
                  {c}
                </button>
              </span>
            ))}
          </nav>
          <span className="kbm-barra-acc">
            <button
              className="btn sm ghost"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(`${location.origin}/bandeja/conocimiento?articulo=${a.id}`);
                  toast('Enlace copiado.');
                } catch {
                  toast('No se pudo copiar el enlace.', 'error');
                }
              }}
            >
              <Icon name="link" /> <span className="lbl-m">Copiar enlace</span>
            </button>
            <button className="btn sm" onClick={editar}>
              <Icon name="edit" /> Editar
            </button>
          </span>
        </div>
        <h2 className="kbm-titulo">{a.titulo}</h2>
        {a.resumen ? <p className="kbm-lead">{a.resumen}</p> : null}
        <div className="kbm-chips">
          {a.etiquetas.map((e) => (
            <span key={e} className="kbm-chip tono-acc">
              {e}
            </span>
          ))}
          {a.actualizado ? (
            <span className="kbm-chip" title={new Date(a.actualizado).toLocaleString('es-CO')}>
              {a.actualizadoPor ? `Actualizado por ${a.actualizadoPor} · ${ago(a.actualizado)}` : `Actualizado ${ago(a.actualizado)}`}
            </span>
          ) : null}
          {a.vistas ? <span className="kbm-chip">{plural(a.vistas, 'consulta', 'consultas')}</span> : null}
        </div>
        {porRevisar(a) ? (
          <div className="md-aviso md-aviso-ojo kbm-revisa" role="note">
            <b className="md-aviso-t">
              <Icon name="alert" size={15} /> Para revisar
            </b>
            <div>
              <p>Este artículo casi no tiene texto (en Zammad venía solo con adjuntos). Si sabes cómo se hace, complétalo con «Editar».</p>
            </div>
          </div>
        ) : null}
        {a.cuerpo.trim() ? <Markdown texto={a.cuerpo} /> : <p className="kbm-sub">Sin contenido todavía.</p>}
        <footer className="kbm-pie">
          <div className="kbm-util" aria-live="polite">
            {voto ? (
              <span>{voto === 'si' ? '¡Gracias! Nos ayuda a saber qué sirve.' : 'Gracias. Si sabes qué falta, edítalo para el próximo.'}</span>
            ) : (
              <>
                <span>¿Te sirvió?</span>
                <button type="button" className="btn" onClick={() => void votar(true)}>
                  Sí
                </button>
                <button type="button" className="btn" onClick={() => void votar(false)}>
                  No, falta algo
                </button>
              </>
            )}
            {voto === 'no' ? (
              <button type="button" className="btn sm ghost" onClick={editar}>
                <Icon name="edit" /> Editar
              </button>
            ) : null}
          </div>
          <button type="button" className="btn primary lg" onClick={() => void insertar()}>
            <Icon name="reply" /> Insertar en la respuesta del caso
          </button>
        </footer>
      </div>
    </article>
  );
}
